import React, { Component } from "react";
import BioinformaticsHub from "bioinformatics-hub";
import calciumSequence from "../../seeds/calciumProteinSequences";
import allMotifs from "./constants/motifs";
import Notification from "../../components/Notification/Notification";
import PredictionResultCards from "../../components/DisplayResult/ShowPredictionResult";
import PredictionSummary from "../../components/DisplayResult/PredictionSummary";
import splitStringToArray from "../../util/splitStringToArray";
import postUsage from "../../util/postUsage";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Form, 
  FormGroup,
  Input,
  Container
} from "reactstrap";
import {ncbiMotifScanner} from "bioinformatics-hub/modules";
import BeatLoader from "react-spinners/BeatLoader";
import "./PredictionBoard.css";

/**
 * provide the web page to predict calcium binding motifs
 */
class CAPS extends Component {
  constructor(props) {
    super(props);
    this.state = {
      backgroundColor: this.props.bgColor, // the background color of side bar
      activeColor: this.props.activeColor, // the active color of buttons and texts
      sequences: calciumSequence, // Sequences
      ncbiAccessionIds: "CAA44792.1, NP_004048.1; P06730.2",
      ncbiAccessionIdArray: null,
      numberOfMaxAllowedIds: 100, // 100 for protein ids and 10 for nucleotide
      motifs: JSON.stringify(allMotifs.calcium_motifs, null, "  "), // String form of the patterns from user input or seeds file
      warning: false,  // determine if warning bar pop up
      warningMessage: "", // the message displayed by warning bar
      predictionResult: null, // the prediction result. The initial state is null
      inputSeqFormat: "fasta", // determine if the input sequence format radio button is checked to "fasta" or "ncbi_protein_id"
      loading: false // showing loading icons if true, false otherwise.
    };
    this.mainPanel = React.createRef();

    this.handleChangeMotifs = this.handleChangeMotifs.bind(this);
    this.clearSequence = this.clearSequence.bind(this);
    this.handleChangeSequences = this.handleChangeSequences.bind(this);

    this.clearNcbiIds = this.clearNcbiIds.bind(this);
    this.handleChangeNcbiIds = this.handleChangeNcbiIds.bind(this);

    this.predictMotifs = this.predictMotifs.bind(this);
    this.onChangeRadioButton = this.onChangeRadioButton.bind(this);
  }

  /**
   * Method to handle the change (switch) of the radio button.
   * @param {event} event 
   */
  onChangeRadioButton (event) {
    this.setState({warning: false, inputSeqFormat: event.target.value, predictionResult: null});
    if (event.target.value === "fasta") { // when user click fasta sequences
      this.setState({
        ncbiAccessionIdArray: null,
        motifs: JSON.stringify(allMotifs.calcium_motifs, null, "  "),
        loading: false});
    }
    if (event.target.value === "ncbi_protein_id") { // when user click NCBI Protein ACCESSION Ids
      const seedsIds = "CAA44792.1, NP_004048.1; P06730.2";
      this.setState({
        numberOfMaxAllowedIds: 100,
        motifs: JSON.stringify(allMotifs.calcium_motifs, null, "  "),
        ncbiAccessionIds: seedsIds,
        ncbiAccessionIdArray: splitStringToArray(seedsIds),
        loading: false
      });
      this.validateMaxAllowedIds(splitStringToArray(this.state.ncbiAccessionIds));
    }
    if (event.target.value === "ncbi_nt_id") { // when user click NCBI nucleotide ACCESSION Ids
      const seedIds = "NM_001305625.2 N89789.1"
      this.setState({
        ncbiAccessionIds: seedIds,
        ncbiAccessionIdArray: splitStringToArray(seedIds),
        numberOfMaxAllowedIds: 10,
        motifs:`{"motif_1":"[A](5)x[GC](6)", "motif2":"ATATATATx(3)AATTA"}`,
        loading: false
      });
      this.validateMaxAllowedIds(splitStringToArray(this.state.ncbiAccessionIds));
    }
  }

  /**
   * Method to handle motif input on change.
   * @param {event} event 
   */
  handleChangeMotifs(event) {
    this.setState({motifs: event.target.value});
  }

  /**
   * Method to handle clear sequence input
   * @param {event} event 
   */
  clearSequence(event) {
    event.preventDefault();
    this.setState({warning: false, sequences: ""});
  }

  /**
   * Method to handle sequence input on change.
   * @param {event} event 
   */
  handleChangeSequences(event) {
    this.setState ({warning: false, sequences: event.target.value})
  }

  /**
   * Method to handle clear NCBI ACCESSION IDs input
   * @param {event} event 
   */
  clearNcbiIds(event) {
    event.preventDefault();
    this.setState({warning: false, ncbiAccessionIds: "", ncbiAccessionIdArray: null});
  }

  /**
   * Method to handle NCBI protein ACCESSION IDs input on change.
   * @param {event} event 
   */
  handleChangeNcbiIds(event) {
    let idArray = null;
    if (event.target.value !== undefined && event.target.value !== null && event.target.value.trim() !== "") {
      idArray = splitStringToArray(event.target.value.trim());
    }
    this.setState ({warning: false, ncbiAccessionIds: event.target.value, ncbiAccessionIdArray: idArray});
    this.validateMaxAllowedIds(idArray);
  }

  /**
   * A helper method to make sure that unique ids from user input do not exceed the allowed number.
   * @param {array} idArray, the id array for validation
   */
  validateMaxAllowedIds(idArray) {
    const idSet = new Set(idArray);
    if (idSet.size > this.state.numberOfMaxAllowedIds) {
      const warningMessage = "The maximum allowed number of NCBI ACCESSION Ids is exceeded. The user should add less than " + this.state.numberOfMaxAllowedIds + " ACCESSION IDs.";
      this.setState({warning: true, warningMessage: warningMessage});
    }
  }

  /**
   * Predict motifs of sequences by scanning motif patterns using bioinformatics-hub
   */
  predictMotifs(event) {
    event.preventDefault();

    // send a post request to record the number of clicks of using this method
    postUsage("CAPS");
    // retrieve and validate input motifs
    let motifString = this.state.motifs; 
    // input motif cannot be null or blank
    if (motifString === undefined || motifString === null || motifString.trim() === "") {
      let warningMessage = "Pattens cannot be empty";
      this.setState({warning: true, warningMessage: warningMessage, predictionResult: null});
      return;
    }
    // validate input pattern format to make sure it can be converted into a non-null pattern
    let motifObject;
    try {
      // convert motif string to jason
      motifObject = JSON.parse(motifString);
    }
    catch(err) {
      let warningMessage = "Invalid pattern format. Please check pattern format in example."; 
      this.setState({warning: true, warningMessage: warningMessage, predictionResult: null});
    }
    if (!motifObject) {
      let warningMessage = "Invalid pattern format. Please check pattern format in example."; 
      this.setState({warning: true, warningMessage: warningMessage, predictionResult: null});
      return;
    }

    // handle the work flow the prediction from fasta sequences
    if (this.state.inputSeqFormat === "fasta") { // handle user defined fasta sequences
      // retrieve and validate input sequences
      let sequences = this.state.sequences;
      // input cannot be null or blank
      if (sequences === undefined || sequences === null || sequences.trim() === "") {
        let warningMessage = "Sequences cannot be empty";
        this.setState({warning: true, warningMessage: warningMessage, predictionResult: null});
        return;
      }

      // now the input pattern and sequence looks good, i can scan motifs from pattern now
      const bioinformaticsHub = new BioinformaticsHub();
      let predictionResult = null;
      try {
        // display loading icon so that if the prediction take time, user know it is loading.
        this.setState({loading: true});
        predictionResult = bioinformaticsHub.setFastaSequences(sequences)
          .getPredictionAssistant()
          .setPatterns(motifObject)
          .predict();
      }
      catch(error){
        this.setState({warning: true, warningMessage: error.message, predictionResult: null, loading: false});
        return;
      }
      this.setState({warning : false, predictionResult: predictionResult, loading: false});
    } else if (this.state.inputSeqFormat === "ncbi_protein_id" || this.state.inputSeqFormat === "ncbi_nt_id") { // handle the input are ncbi sequence ids.
      let idString = this.state.ncbiAccessionIds;
      let idArray = this.state.ncbiAccessionIdArray;
      // input cannot be null or blank
      if (idString === undefined || idString === null || idString.trim() === "" || idArray === null || idArray.length === 0) {
        let warningMessage = "NCBI ACCESSION Ids input field cannot be empty.";
        this.setState({warning: true, warningMessage: warningMessage, predictionResult: null});
        return;
      }
      // now the input pattern and sequence looks good, i can scan motifs from pattern now
      if (this.state.inputSeqFormat === "ncbi_protein_id"){ // scan motifs in proteins
        //change the loading state to be true for loading data
        this.setState({loading: true});

        ncbiMotifScanner.scanProteinMotifsWithNcbiIds(idArray, motifObject).then((value) =>{
          this.setState({warning : false, predictionResult: value, loading: false});
        }).catch((error)=>{
          this.setState({warning: true, warningMessage: error.message, predictionResult: null, loading: false});
        });
      } else { // scan motifs in nucleotide
        //change the loading state to be true for loading data
        this.setState({loading: true});

        ncbiMotifScanner.scanNucleotideMotifsWithNcbiIds(idArray, motifObject).then((value) =>{
          // print the whole output object
          console.log(value);
          this.setState({warning : false, predictionResult: value, loading: false});
        }).catch((error)=>{
          this.setState({warning: true, warningMessage: error.message, predictionResult: null, loading: false});
        });
      }
    } else {
      this.setState({warning: true, warningMessage: "Something is wrong, please refresh the page.", predictionResult: null, loading: false});
    }
  }

  render() {
    let textColor = "text-" + this.props.activeColor;

    const seqInputForm = <div style={{marginTop: "2rem"}}>
      <h6 className={textColor}>Sequences</h6>
        <p>One or multiple sequences should be listed below with 
          <a target="_blank" 
            rel="noopener noreferrer" 
            href="https://blast.ncbi.nlm.nih.gov/Blast.cgi?CMD=Web&PAGE_TYPE=BlastDocs&DOC_TYPE=BlastHelp"> FASTA
          </a> format. There is no maximum number of sequences in this input.
        </p>
        <Input
          type="textarea"
          value={this.state.sequences}
          style={{fontFamily: "Monospace"}}
          id="inputForm2"
          onChange={this.handleChangeSequences}
        />
      </div>
    const ncbiAccessionIdInputForm = <div style={{marginTop: "2rem"}}>
      <h6 className={textColor}>NCBI Accession IDs</h6>
        <p>One or multiple NCBI Accession IDs should be listed below with separated by ";" or ","  or blank space. User can list up to 100 unique NCBI protein ACCESSION IDs.<br />
        </p>
        <Input
          type="textarea"
          value={this.state.ncbiAccessionIds}
          style={{fontFamily: "Monospace"}}
          id="inputForm3"
          onChange={this.handleChangeNcbiIds}
        />
        {this.state.ncbiAccessionIdArray && this.state.ncbiAccessionIdArray.length !== 0 &&
          <p style={{marginTop: "1rem"}}>The input NCBI ACCESSION ID(s): {this.state.ncbiAccessionIdArray.join(", ")}</p>
        }
      </div>
    return (
        <div className="content">
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle tag="h3" className={textColor}>CAPS: Scan calcium binding motifs in protein sequences</CardTitle>
                </CardHeader>
                <CardBody>
                  <Alert
                    className="alert-with-icon"
                    color={this.props.activeColor}
                    fade={false}
                  >
                    <span
                      data-notify="icon"
                      className="far fa-question-circle"
                    />
                    <span data-notify="message">
                      Predict calcium binding motifs (classical and pseudo EF-hand binding motifs) in multiple protein sequences.
                      We accept <strong>PROTEIN sequences</strong> in FASTA format or NCBI ACCESSION IDs.
                    </span>
                  </Alert>
                  <p>CaPS (Calcium Pattern Search) identifies Ca(II)-binding motifs in proteins based on unique patterns (or signatures) listed below. These patterns (or signatures) can be used to identify canonical EF-hand and pseudo EF-hand. We expect to significantly expand this list in the future to include non-contiguous (binding motif includes AA residues distant from each other in the primary structure) patterns. To check a sequence for the presence of a Ca(II)-binding signature, please past the protein sequences or NCBI protein accession Ids in the textarea and then click the <strong>Submit button</strong>.</p>
                  <p>For more details, please read: </p>
                  <p> Zhou Y, Yang W, Kirberger M, Lee HW, Ayalasomayajula G, Yang JJ.
                    <a href="https://onlinelibrary.wiley.com/doi/abs/10.1002/prot.21139" target="_blank" rel="noopener noreferrer">
                      Prediction of EF-hand calcium-binding proteins and analysis of bacterial EF-hand proteins.
                      <b> Proteins</b>. 2006 Nov 15;65(3):643-55.
                    </a> 
                    <br />
                    Zhou Y, Xue S, Yang JJ.
                    <a href="http://pubs.rsc.org/en/Content/ArticleLanding/2013/MT/C2MT20009K#!divAbstract" target="_blank" rel="noopener noreferrer"> Calciomics: integrative studies of Ca2+-binding proteins and their interactomes in biological systems.
                    <b> Metallomics</b>. 2013 Jan;5(1):29-42.
                    </a>
                    <br />
                    Zhou Y, Xue S, Chen Y, Yang JJ.
                    <a href="https://link.springer.com/protocol/10.1007%2F978-1-62703-230-8_3" target="_blank" rel="noopener noreferrer"> Probing Ca2+-binding capability of viral proteins with the EF-hand motif by grafting approach.
                      <b> Methods Mol Biol</b>. 2013;963:37-53
                    </a>
                    <br />
                    Chen Y, Xue S, Zhou Y, Yang JJ.
                    <a href="https://link.springer.com/article/10.1007/s11426-010-0011-5" target="_blank" rel="noopener noreferrer"> Calciomics: prediction and analysis of EF-hand calcium binding proteins by protein engineering.
                    <b> Sci China Chem</b>. 2010 Jan 1;53(1):52-60.
                    </a>
                  </p>
                  <img className="col-sm-12" src="https://shenghuixue.github.io/CAPS/eftable.png" alt="EF-hand table"/>
                  <Form>
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          {
                            this.state.inputSeqFormat === "fasta" ? seqInputForm : ncbiAccessionIdInputForm
                          }
                          <p/ >
                        </FormGroup>
                      </Col>
                    </Row>
                    <Container fluid style = {{paddingLeft: "2rem"}} onChange={this.onChangeRadioButton}>
                      <div className="form-check">
                        <input 
                          className="form-check-input" type="radio" name="inputSeqFormat" id="exampleRadios1" value="fasta" defaultChecked={this.state.inputSeqFormat === "fasta"}/>
                        <label className={textColor + " font-weight-bold"} htmlFor="exampleRadios1">
                          FASTA sequences
                        </label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="inputSeqFormat" id="exampleRadios2" value="ncbi_protein_id" defaultChecked={this.state.inputSeqFormat === "ncbi_protein_id"} />
                        <label className={textColor + " font-weight-bold"} htmlFor="exampleRadios2">
                          NCBI <strong>Protein</strong> ACCESSION Ids
                        </label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="inputSeqFormat" id="exampleRadios3" value="ncbi_nt_id" defaultChecked={this.state.inputSeqFormat === "ncbi_nt_id"} disabled/>
                        <label className={textColor + " font-weight-bold"} htmlFor="exampleRadios2">
                          NCBI <strong>Nucleotide</strong> ACCESSION Ids
                        </label>
                      </div>
                    </Container>
                    {this.state.warning && 
                      <Notification 
                        color={this.props.activeColor === "danger" ? "success" : "danger"}
                        message={this.state.warningMessage}
                      />
                    }
                    <div className="container-fluid">
                      {
                        this.state.inputSeqFormat === "fasta" ?
                        <Button
                          className="btn-round"
                          color={this.props.activeColor === "danger" ? "success" : "danger"}
                          onClick={this.clearSequence}
                        >
                          Clear Sequences
                        </Button> : 
                        <Button
                          className="btn-round"
                          color={this.props.activeColor === "danger" ? "success" : "danger"}
                          onClick={this.clearNcbiIds}
                        >
                          Clear ACCESSION IDs
                        </Button>
                      }
                      <Button
                        className="btn-round"
                        color={this.props.activeColor}
                        type="submit"
                        onClick = {this.predictMotifs}
                      >
                        Submit
                      </Button> 
                    </div>
                  </Form>
                  <Container>
                    <BeatLoader
                      size={15}
                      color={"#7ed6a5"}
                      loading={this.state.loading}
                      style={{marginLeft: "30rem"}}
                    />
                  </Container>
                </CardBody>
              </Card>
              {this.state.predictionResult !== null && (
                <div>
                  <PredictionSummary
                    activeColor={this.props.activeColor} 
                    predictionResult={this.state.predictionResult}
                  />
                  <PredictionResultCards 
                    activeColor={this.props.activeColor} 
                    predictionResult={this.state.predictionResult}
                  />
                </div>)
              }
            </Col>
          </Row>
        </div>
    );
  }
}

export default CAPS;
